import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Container,
    Heading,
    VStack,
    FormControl,
    FormLabel,
    Input,
    Button,
    Text,
    SimpleGrid,
    HStack,
    Card,
    CardBody,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Badge,
    Flex,
} from '@chakra-ui/react';
import { QRCodeCanvas } from 'qrcode.react';
import { getPaynowQRCode } from './utils/paynowQr';
import { COLORS } from './styles/colors';

const toPascalCaseRef = (value) =>
    value
        .toLowerCase()
        .replace(/(?:^|\s|[^a-zA-Z0-9])+(.)/g, (match, char) => char.toUpperCase())
        .replace(/[^a-zA-Z0-9]/g, '');

// Shared PayNow branding for all tenants (public/paynow-logo.png)
const PAYNOW_PURPLE = '#901880';
const PAYNOW_QR_BG = '#FFFFFF';
const PAYNOW_LOGO_SRC = '/paynow-logo.png';

const fieldLabelProps = {
    fontSize: 'sm',
    fontWeight: 'semibold',
    color: COLORS.maastrichtBlue,
    mb: 1.5,
};

const inputProps = {
    bg: 'white',
    borderColor: 'gray.200',
    borderRadius: 'md',
    size: 'md',
    _hover: { borderColor: 'gray.300' },
    _focus: {
        borderColor: COLORS.mandarin,
        boxShadow: `0 0 0 1px ${COLORS.mandarin}`,
    },
};

export const QRTesting = () => {
    const [paymentType, setPaymentType] = useState('uen');
    const [amount, setAmount] = useState('');
    const [uen, setUen] = useState('201940174R');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [qrCodeData, setQrCodeData] = useState('');
    const [error, setError] = useState('');
    const [encodedSummary, setEncodedSummary] = useState(null);
    const qrCanvasRef = useRef(null);

    const pascalCaseRef = referenceNumber ? toPascalCaseRef(referenceNumber) : '';
    const refCharCount = pascalCaseRef.length;
    const isRefOverLimit = refCharCount > 25;

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                setError('');

                const config = {};

                if (paymentType === 'uen') {
                    if (!uen.trim()) {
                        setQrCodeData('');
                        setEncodedSummary(null);
                        return;
                    }
                    config.uen = uen.trim();
                } else {
                    if (!phoneNumber.trim()) {
                        setQrCodeData('');
                        setEncodedSummary(null);
                        return;
                    }
                    config.phone = phoneNumber.startsWith('+65')
                        ? phoneNumber.trim()
                        : `+65${phoneNumber.trim()}`;
                }

                if (amount && parseFloat(amount) > 0) {
                    config.amount = parseFloat(amount).toFixed(2);
                }

                if (referenceNumber.trim()) {
                    const ref = toPascalCaseRef(referenceNumber);
                    if (ref.length > 25) {
                        setError(`Reference must be 25 characters or less (currently ${ref.length})`);
                        setQrCodeData('');
                        setEncodedSummary(null);
                        return;
                    }
                    if (ref) config.referenceNumber = ref;
                }

                setQrCodeData(getPaynowQRCode(config));
                setEncodedSummary({
                    payTo: config.uen || config.phone,
                    amount: config.amount || 'Open',
                    reference: config.referenceNumber || '—',
                });
            } catch (err) {
                console.error('Error generating QR code:', err);
                setError(`Error: ${err.message}`);
                setQrCodeData('');
                setEncodedSummary(null);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [paymentType, uen, phoneNumber, amount, referenceNumber]);

    const downloadQrPng = () => {
        const canvas = qrCanvasRef.current;
        if (!canvas) return;

        const refSlug = (referenceNumber || 'PayNow')
            .replace(/[^a-zA-Z0-9]+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 24) || 'PayNow';

        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = `PayNow-QR-${refSlug}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box minH="100vh" bg={COLORS.isabelline} py={{ base: 6, md: 10 }}>
            <Container maxW="960px">
                <VStack spacing={6} align="stretch">
                    <Box>
                        <Heading
                            size="lg"
                            color={COLORS.maastrichtBlue}
                            letterSpacing="-0.02em"
                        >
                            PayNow QR Code Generator
                        </Heading>
                        <Text mt={1} fontSize="sm" color={COLORS.shadowBlue}>
                            Edit the fields: the QR updates automatically
                        </Text>
                    </Box>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} alignItems="start">
                        <Card
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.100"
                            borderRadius="xl"
                            boxShadow="sm"
                        >
                            <CardBody p={{ base: 4, md: 5 }}>
                                <VStack spacing={4} align="stretch">
                                    <Text
                                        fontSize="xs"
                                        fontWeight="bold"
                                        letterSpacing="0.06em"
                                        textTransform="uppercase"
                                        color={COLORS.shadowBlue}
                                    >
                                        Payment details
                                    </Text>

                                    <Tabs
                                        variant="soft-rounded"
                                        colorScheme="orange"
                                        size="sm"
                                        index={paymentType === 'uen' ? 0 : 1}
                                        onChange={(index) =>
                                            setPaymentType(index === 0 ? 'uen' : 'phone')
                                        }
                                    >
                                        <TabList
                                            bg="gray.50"
                                            p={1}
                                            borderRadius="lg"
                                            gap={1}
                                        >
                                            <Tab
                                                flex={1}
                                                fontWeight="semibold"
                                                _selected={{
                                                    bg: 'white',
                                                    color: COLORS.mandarin,
                                                    boxShadow: 'sm',
                                                }}
                                            >
                                                UEN
                                            </Tab>
                                            <Tab
                                                flex={1}
                                                fontWeight="semibold"
                                                _selected={{
                                                    bg: 'white',
                                                    color: COLORS.mandarin,
                                                    boxShadow: 'sm',
                                                }}
                                            >
                                                Phone
                                            </Tab>
                                        </TabList>

                                        <TabPanels>
                                            <TabPanel px={0} pt={3} pb={0}>
                                                <FormControl isRequired>
                                                    <FormLabel {...fieldLabelProps}>
                                                        UEN Number
                                                    </FormLabel>
                                                    <Input
                                                        {...inputProps}
                                                        placeholder="e.g. 201940174R"
                                                        value={uen}
                                                        onChange={(e) => setUen(e.target.value)}
                                                        fontFamily="mono"
                                                    />
                                                </FormControl>
                                            </TabPanel>

                                            <TabPanel px={0} pt={3} pb={0}>
                                                <FormControl isRequired>
                                                    <FormLabel {...fieldLabelProps}>
                                                        Phone Number
                                                    </FormLabel>
                                                    <Input
                                                        {...inputProps}
                                                        placeholder="e.g. 91234567"
                                                        value={phoneNumber}
                                                        onChange={(e) =>
                                                            setPhoneNumber(e.target.value)
                                                        }
                                                        fontFamily="mono"
                                                    />
                                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                                        +65 added automatically if missing
                                                    </Text>
                                                </FormControl>
                                            </TabPanel>
                                        </TabPanels>
                                    </Tabs>

                                    <FormControl>
                                        <FormLabel {...fieldLabelProps}>Amount (SGD)</FormLabel>
                                        <Input
                                            {...inputProps}
                                            type="number"
                                            step="0.01"
                                            placeholder="Leave empty for open amount"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <Flex justify="space-between" align="baseline" mb={1.5}>
                                            <FormLabel {...fieldLabelProps} mb={0}>
                                                Reference
                                            </FormLabel>
                                            {referenceNumber && (
                                                <Text
                                                    fontSize="xs"
                                                    fontWeight="semibold"
                                                    color={isRefOverLimit ? 'red.500' : 'gray.500'}
                                                >
                                                    {refCharCount}/25
                                                </Text>
                                            )}
                                        </Flex>
                                        <Input
                                            {...inputProps}
                                            placeholder="e.g. John Doe Full Cert"
                                            value={referenceNumber}
                                            onChange={(e) => setReferenceNumber(e.target.value)}
                                            isInvalid={isRefOverLimit}
                                        />
                                        {referenceNumber && (
                                            <Text
                                                mt={1.5}
                                                fontSize="xs"
                                                fontFamily="mono"
                                                color={isRefOverLimit ? 'red.500' : COLORS.shadowBlue}
                                            >
                                                Sent as:{' '}
                                                <Text as="span" fontWeight="bold" color={COLORS.maastrichtBlue}>
                                                    {pascalCaseRef || '(empty)'}
                                                </Text>
                                            </Text>
                                        )}
                                    </FormControl>

                                    {error && (
                                        <Box
                                            px={3}
                                            py={2}
                                            bg="red.50"
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor="red.100"
                                        >
                                            <Text color="red.600" fontSize="sm">
                                                {error}
                                            </Text>
                                        </Box>
                                    )}
                                </VStack>
                            </CardBody>
                        </Card>

                        <Card
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.100"
                            borderRadius="xl"
                            boxShadow="sm"
                            position={{ lg: 'sticky' }}
                            top={{ lg: 6 }}
                        >
                            <CardBody p={{ base: 4, md: 5 }}>
                                <VStack spacing={4} align="stretch">
                                    <Flex justify="space-between" align="center">
                                        <Text
                                            fontSize="xs"
                                            fontWeight="bold"
                                            letterSpacing="0.06em"
                                            textTransform="uppercase"
                                            color={COLORS.shadowBlue}
                                        >
                                            QR preview
                                        </Text>
                                        {qrCodeData && (
                                            <Badge
                                                colorScheme="green"
                                                variant="subtle"
                                                borderRadius="md"
                                            >
                                                Live
                                            </Badge>
                                        )}
                                    </Flex>

                                    {qrCodeData ? (
                                        <VStack spacing={4}>
                                            <Flex
                                                justify="center"
                                                w="100%"
                                                py={4}
                                                bg="gray.50"
                                                borderRadius="lg"
                                            >
                                                <Box
                                                    p={3}
                                                    bg="white"
                                                    borderRadius="md"
                                                    borderWidth="1px"
                                                    borderColor="gray.200"
                                                >
                                                    <QRCodeCanvas
                                                        ref={qrCanvasRef}
                                                        value={qrCodeData}
                                                        size={220}
                                                        level="H"
                                                        includeMargin={true}
                                                        fgColor={PAYNOW_PURPLE}
                                                        bgColor={PAYNOW_QR_BG}
                                                        imageSettings={{
                                                            src: PAYNOW_LOGO_SRC,
                                                            width: 56,
                                                            height: 38,
                                                            excavate: true,
                                                        }}
                                                    />
                                                </Box>
                                            </Flex>

                                            {encodedSummary && (
                                                <SimpleGrid columns={1} spacing={2} w="100%">
                                                    <HStack
                                                        justify="space-between"
                                                        fontSize="sm"
                                                        px={1}
                                                    >
                                                        <Text color="gray.500">Pay to</Text>
                                                        <Text
                                                            fontFamily="mono"
                                                            fontWeight="semibold"
                                                            color={COLORS.maastrichtBlue}
                                                        >
                                                            {encodedSummary.payTo}
                                                        </Text>
                                                    </HStack>
                                                    <HStack
                                                        justify="space-between"
                                                        fontSize="sm"
                                                        px={1}
                                                    >
                                                        <Text color="gray.500">Amount</Text>
                                                        <Text fontWeight="semibold" color={COLORS.maastrichtBlue}>
                                                            {encodedSummary.amount === 'Open'
                                                                ? 'Open amount'
                                                                : `S$ ${encodedSummary.amount}`}
                                                        </Text>
                                                    </HStack>
                                                    <HStack
                                                        justify="space-between"
                                                        fontSize="sm"
                                                        px={1}
                                                    >
                                                        <Text color="gray.500">Reference</Text>
                                                        <Text
                                                            fontFamily="mono"
                                                            fontWeight="semibold"
                                                            color={COLORS.maastrichtBlue}
                                                            textAlign="right"
                                                            maxW="60%"
                                                            wordBreak="break-all"
                                                        >
                                                            {encodedSummary.reference}
                                                        </Text>
                                                    </HStack>
                                                </SimpleGrid>
                                            )}

                                            <Button
                                                w="100%"
                                                bg={COLORS.mandarin}
                                                color="white"
                                                _hover={{ bg: '#D66A3A' }}
                                                _active={{ bg: '#C45F34' }}
                                                onClick={downloadQrPng}
                                            >
                                                Download PNG
                                            </Button>
                                        </VStack>
                                    ) : (
                                        <Flex
                                            minH="280px"
                                            align="center"
                                            justify="center"
                                            borderWidth="1px"
                                            borderStyle="dashed"
                                            borderColor="gray.200"
                                            borderRadius="lg"
                                            bg="gray.50"
                                            px={6}
                                        >
                                            <Text color="gray.500" fontSize="sm" textAlign="center">
                                                Enter a UEN or phone number to preview the QR
                                            </Text>
                                        </Flex>
                                    )}
                                </VStack>
                            </CardBody>
                        </Card>
                    </SimpleGrid>
                </VStack>
            </Container>
        </Box>
    );
};

export default QRTesting;
